import type {
  Arrangement,
  CubeHellPlayerId,
} from "@cloudnest/cube-hell-engine";
import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { CubeHellView } from "./CubeHellView";
import { CUBE_HELL_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  buildCubeHellReplaySnapshots,
  type CubeHellReplaySnapshot,
} from "./replay-runner";
import "./styles/cube-hell-tokens.css";

interface ReplayScreenProps {
  readonly scenario: string;
}

const SPEED_OPTIONS = [
  { label: "느리게", ms: 900 },
  { label: "보통", ms: 500 },
  { label: "빠르게", ms: 250 },
] as const;

const EVENT_LABEL: Record<string, string> = {
  init: "init",
  QueryCell: "칸 질문",
  QueryLayer: "층 질문",
  Guess: "추리 선언",
};

function clampStepIndex(
  index: number,
  snapshots: readonly CubeHellReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly CubeHellReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly CubeHellReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

function eventSummary(
  snapshot: CubeHellReplaySnapshot,
  names: Record<string, string>,
): string {
  const event = snapshot.event;
  if (event.eventType === "init") return `#${event.sequenceNumber} init`;
  const who = names[event.userId] ?? event.userId;
  const label = EVENT_LABEL[event.eventType] ?? event.eventType;
  return `#${event.sequenceNumber} ${who} ${label}`;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = CUBE_HELL_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildCubeHellReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const namesByPlayerId = useMemo(
    () =>
      fixture
        ? Object.fromEntries(
            fixture.players.map((player) => [player.id, player.name]),
          )
        : {},
    [fixture],
  );

  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const [speedMs, setSpeedMs] = useState<number>(SPEED_OPTIONS[1].ms);
  const [showAll, setShowAll] = useState(false);

  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;
  const maxIndex = snapshots.length - 1;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  useEffect(() => {
    if (!isPlaying) return;
    if (safeStepIndex >= maxIndex) {
      setIsPlaying(false);
      return;
    }
    const timer = setTimeout(() => {
      const next = clampStepIndex(safeStepIndex + 1, snapshots);
      setStepIndex(next);
      replaceStepQuery(next, snapshots);
    }, speedMs);
    return () => clearTimeout(timer);
  }, [isPlaying, safeStepIndex, maxIndex, speedMs, snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const viewer = current.viewer;
  const isGameOver = state.phase === "game-over";

  // Default: observer projection (both arrangements masked). Recap: true cubes.
  const truth: Record<CubeHellPlayerId, Arrangement | null> = showAll
    ? { p1: state.arrangements.p1, p2: state.arrangements.p2 }
    : { p1: viewer.arrangements.p1, p2: viewer.arrangements.p2 };

  const names = {
    p1: namesByPlayerId.p1 ?? "선",
    p2: namesByPlayerId.p2 ?? "후",
  };

  return (
    <div
      data-testid="cube-hell-replay-screen"
      className="min-h-screen px-3 py-4 sm:px-6"
      style={{ background: "var(--cn-cube-hell-bg)" }}
    >
      <div className="mx-auto flex max-w-xl flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="m-0 font-bold text-lg text-text-primary">
              큐브지옥 리플레이
            </h1>
            <p className="mt-0.5 text-text-muted text-xs">
              {fixture.players.map((p) => p.name).join(" vs ")} · 시뮬레이션
            </p>
          </div>
          <button
            type="button"
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
            onClick={() => navigateTo("/")}
          >
            ‹ 뒤로
          </button>
        </div>

        {isGameOver ? (
          <div
            data-testid="cube-hell-replay-result"
            className="rounded-md border border-accent-blue-light bg-accent-blue-bg px-4 py-3 text-center font-semibold text-accent-blue text-sm"
          >
            {state.winnerId
              ? `🏆 ${names[state.winnerId]} 완전정답 승리 (총 ${state.moveNumber}수)`
              : "무승부"}
          </div>
        ) : (
          <div className="rounded-md border border-border-subtle bg-surface-secondary px-4 py-2 text-center text-sm text-text-secondary">
            현재 차례:{" "}
            <span className="font-semibold">
              {names[state.currentPlayerId] ?? state.currentPlayerId}
            </span>
          </div>
        )}

        <div className="flex justify-center">
          <CubeHellView
            phase={state.phase}
            currentPlayerId={state.currentPlayerId}
            queryCounts={state.queryCounts}
            log={state.log}
            names={names}
            truth={truth}
            winnerId={state.winnerId}
          />
        </div>

        <div className="flex items-center justify-center">
          <button
            type="button"
            data-testid="cube-hell-replay-recap-toggle"
            data-active={showAll ? "true" : "false"}
            onClick={() => setShowAll((shown) => !shown)}
            className="rounded-md border border-border-primary bg-surface-secondary px-3 py-1.5 text-text-secondary text-xs"
          >
            {showAll ? "배열 숨김 (관전 뷰)" : "정답 배열 공개 (복기)"}
          </button>
        </div>

        <div
          data-testid="cube-hell-replay-toolbar"
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-secondary p-3"
        >
          <p
            data-testid="cube-hell-replay-event-summary"
            className="text-center text-sm text-text-primary"
          >
            {eventSummary(current, namesByPlayerId)}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="cube-hell-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex - 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              ◀ 이전
            </button>
            <button
              type="button"
              data-testid="cube-hell-replay-play-toggle"
              onClick={() => setIsPlaying((playing) => !playing)}
              disabled={safeStepIndex >= maxIndex}
              className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white disabled:opacity-40"
            >
              {isPlaying ? "⏸ 일시정지" : "▶ 자동재생"}
            </button>
            <button
              type="button"
              data-testid="cube-hell-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => {
                setIsPlaying(false);
                setReplayStep(safeStepIndex + 1);
              }}
              className="rounded-md border border-border-primary bg-surface-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
            >
              다음 ▶
            </button>
            <span
              data-testid="cube-hell-replay-step-count"
              className="ml-auto text-text-secondary text-xs"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxIndex}
            value={safeStepIndex}
            data-testid="cube-hell-replay-step-slider"
            onChange={(event) => {
              setIsPlaying(false);
              setReplayStep(Number(event.target.value));
            }}
            className="w-full"
          />
          <label className="flex items-center justify-end gap-2 text-text-muted text-xs">
            재생 속도
            <select
              data-testid="cube-hell-replay-speed"
              value={speedMs}
              onChange={(event) => setSpeedMs(Number(event.target.value))}
              className="rounded-md border border-border-primary bg-surface-primary px-2 py-1 text-text-primary"
            >
              {SPEED_OPTIONS.map((option) => (
                <option key={option.ms} value={option.ms}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
    </div>
  );
}
